import { onMounted, readonly, ref } from "vue"
import { finalizeZoomClose } from "@/composables/use-zoom"

export enum StudentPageState {
  GALLERY,
  INDEX,
  ZOOMFLGHT,
  ZOOM,
  // Appended last on purpose: StudentGalleryWrapper's `heights`/ResizeObserver
  // logic is keyed by the numeric values of GALLERY (0) and INDEX (1).
  LOADING,
}

export enum PageEvent {
  OPEN_INDEX,
  CLOSE_INDEX,
  OPEN_ZOOM,
  CLOSE_ZOOM,
  FLIGHT_COMPLETE,
  GALLERY_LOADED,
}

interface Transition {
  target:
    | StudentPageState
    | ((previousHistory: StudentPageState) => StudentPageState)
  onExit?: () => void
  onEnter?: () => void
  preserveHistory?: boolean
}

const transitions: Record<
  StudentPageState,
  Partial<Record<PageEvent, Transition>>
> = {
  [StudentPageState.GALLERY]: {
    [PageEvent.OPEN_INDEX]: { target: StudentPageState.INDEX },
    [PageEvent.OPEN_ZOOM]: {
      target: StudentPageState.ZOOMFLGHT,
      onEnter: () => {
        document.body.style.overflow = "hidden"
        flightDirection.value = "in"
        mountFlight()
      },
    },
  },
  [StudentPageState.INDEX]: {
    [PageEvent.CLOSE_INDEX]: { target: StudentPageState.GALLERY },
    [PageEvent.OPEN_ZOOM]: {
      target: StudentPageState.ZOOMFLGHT,
      onEnter: () => {
        document.body.style.overflow = "hidden"
        flightDirection.value = "in"
        mountFlight()
      },
    },
  },
  [StudentPageState.ZOOMFLGHT]: {
    [PageEvent.FLIGHT_COMPLETE]: {
      target: (prev: StudentPageState) =>
        flightDirection.value === "out" ? prev : StudentPageState.ZOOM,
      preserveHistory: true,
      onEnter: () => {
        if (flightDirection.value === "out") {
          // Keep the landed clone on screen until GALLERY/INDEX has fully faded
          // back in, then drop it — otherwise the image flashes out mid-fade.
          flightUnmountTimer = setTimeout(() => {
            flightMounted.value = false
            flightUnmountTimer = null
            document.body.style.overflow = ""
          }, PANEL_FADE_MS)
        } else {
          // Going in: StudentZoom (z-60, opaque) covers the flight immediately.
          flightMounted.value = false
        }
      },
    },
  },
  [StudentPageState.ZOOM]: {
    [PageEvent.CLOSE_ZOOM]: {
      // Route back out through ZOOMFLGHT so the reverse flight can play.
      target: StudentPageState.ZOOMFLGHT,
      preserveHistory: true,
      onEnter: () => {
        document.body.style.overflow = "hidden"
        flightDirection.value = "out"
        mountFlight()
      },
    },
    [PageEvent.CLOSE_INDEX]: {
      // Legacy direct exit — no flight, so tear the zoom state down here.
      target: StudentPageState.GALLERY,
      onEnter: () => {
        document.body.style.overflow = ""
        finalizeZoomClose()
      },
    },
  },
  [StudentPageState.LOADING]: {
    [PageEvent.GALLERY_LOADED]: {
      // Reveal the gallery once its initial slides have loaded. preserveHistory
      // keeps `history` at GALLERY so the zoom-flight restore never sees LOADING.
      target: StudentPageState.GALLERY,
      preserveHistory: true,
    },
  },
}

// ─── Singleton state ──────────────────────────────────────────────────────────

const pageMode = ref<StudentPageState>(StudentPageState.LOADING)
const history = ref<StudentPageState>(StudentPageState.GALLERY)
// Which way the shared ZOOMFLGHT flight is travelling: "in" → ZOOM, "out" → back to GALLERY/INDEX.
const flightDirection = ref<"in" | "out">("in")

// ZoomFlight's mount is deliberately decoupled from `pageMode === ZOOMFLGHT`: on
// the way out it must linger over the GALLERY/INDEX opacity fade so the landed
// clone doesn't flash away before the real thumbnail is opaque.
const flightMounted = ref(false)
let flightUnmountTimer: ReturnType<typeof setTimeout> | null = null
// Matches the `.panel-transition` opacity duration in StudentGalleryWrapper.vue
const PANEL_FADE_MS = 300

const mountFlight = () => {
  if (flightUnmountTimer) {
    clearTimeout(flightUnmountTimer)
    flightUnmountTimer = null
  }
  flightMounted.value = true
}

export const resetStudentPageState = () => {
  pageMode.value = StudentPageState.LOADING
  history.value = StudentPageState.GALLERY
  flightMounted.value = false
  flightDirection.value = "in"
  if (flightUnmountTimer) {
    clearTimeout(flightUnmountTimer)
    flightUnmountTimer = null
  }
}

// ─── Composable ───────────────────────────────────────────────────────────────

export const useStudentPageTransitions = () => {
  const send = (event: PageEvent) => {
    const transition = transitions[pageMode.value][event]
    if (!transition) return
    transition.onExit?.()
    const previousHistory = history.value
    if (!transition.preserveHistory) {
      history.value = pageMode.value
    }

    const resolvedTarget =
      typeof transition.target === "function"
        ? transition.target(previousHistory)
        : transition.target

    if (window.scrollY !== 0) {
      window.addEventListener(
        "scrollend",
        async () => {
          transition.onEnter?.()
          // await new Promise(resolve => setTimeout(resolve, 300))
          pageMode.value = resolvedTarget
        },
        { once: true },
      )
      window.scrollTo({ top: 0, behavior: "smooth" })
    } else {
      transition.onEnter?.()
      pageMode.value = resolvedTarget
    }
  }

  return {
    pageMode: readonly(pageMode),
    send,
    flightDirection: readonly(flightDirection),
    flightMounted: readonly(flightMounted),
    history: readonly(history),
  }
}
